import clsx from 'clsx';
import React from 'react';

import BaseButton, { BaseButtonProps } from '../DesignSystem/BaseButton/BaseButton';
import IconCommon, { DesignSystemIconType } from '@/components/Common/DesignSystem/Icon/IconCommon';
import TextButton from '../DesignSystem/TextButton/TextButton';
import SunnyLink, { ISunnyLinkProps } from '../SunnyLink';
import styles from './SubHeader.module.scss';

interface ISubHeaderProps {
  children: SubHeaderChildren;
  className?: string;
}

type SubHeaderChildren =
  | {
      title: JSX.Element | string;
      subtitle?: JSX.Element | string | null | false;
      /**
       * 우측에 배치할 요소.
       *
       * 단순 문자열이면 '인포텍스트' 스타일을 사용한다.
       *
       * `SubHeader`에 사전 정의된 `<IconButton>`, `<TextButton>`이 존재한다.
       * @see https://www.figma.com/file/UCKulsei4QcUxxGxECOXiU/Yanolja-Design-Kit?type=design&node-id=1003-8886&t=UfxoDh2OEgBm9817-0
       */
      aside?: JSX.Element | string | null | false;
    }
  | string;

type SubHeaderComponent = React.FC<ISubHeaderProps> & {
  IconButton: React.FC<BaseButtonProps & { children?: never; iconType: DesignSystemIconType }>;
  /** 텍스트만으로 이루어진 우측 버튼. */
  TextButton: typeof TextButton.Secondary;
  /**
   * 텍스트와 화살표로 이루어진 우측 버튼.
   * @deprecated
   */
  ActionText: React.FC<ISunnyLinkProps & React.RefAttributes<HTMLAnchorElement>>;
};

/**
 * "섹션 타이틀" 컴포넌트. [Figma](https://www.figma.com/file/UCKulsei4QcUxxGxECOXiU/Yanolja-Design-Kit?type=design&node-id=1003-8886&t=UfxoDh2OEgBm9817-0)
 *
 * ```tsx
 * // 제목만 사용할 경우
 * <SubHeader>제목</SubHeader>
 *
 * // 부제목, 우측에 버튼까지 추가할 경우
 * <SubHeader>
 *   {{
 *     title: '제목',
 *     subtitle: '부제목 No.1 NOL',
 *     aside: (
 *       // `<SubHeader.TextButton>` 외에도 일반 문자열, `<SubHeader.IconButton>`, 혹은 기타 컴포넌트 사용 가능.
 *       <SubHeader.TextButton as="link" className={styles.foo} href="/bar" onClick={baz}>전체 보기</SubHeader.PlainText>
 *     ),
 *   }}
 * </SubHeader>
 * ```
 */
const SubHeader: SubHeaderComponent = ({ children, className }) => {
  if (typeof children === 'string') {
    return (
      <header className={clsx(styles.container, styles.titleWrap, className)}>
        <h2 className={styles.title}>{children}</h2>
      </header>
    );
  }

  if ('title' in children) {
    const { aside } = children;

    return (
      <header className={clsx(styles.container, className)}>
        <div className={children.subtitle ? styles.titleWrapWithSubtitle : styles.titleWrap}>
          <h2 className={styles.title}>{children.title}</h2>
          {children.subtitle && typeof children.subtitle === 'string' && (
            <p className={styles.subtitle}>{children.subtitle}</p>
          )}
          {children.subtitle && typeof children.subtitle !== 'string' && (
            <div className={styles.subtitle}>{children.subtitle}</div>
          )}
        </div>
        {aside && (
          <div className={styles.aside}>
            {typeof aside === 'string' ? <span className={styles.infoText}>{aside}</span> : aside}
          </div>
        )}
      </header>
    );
  }

  return null;
};
SubHeader.IconButton = ({ className, iconType, ...props }) => {
  // 헤더에 얇은 아이콘이 오지는 않을 것이라 기대...
  return (
    <BaseButton className={clsx(styles.iconButton, className)} {...props}>
      <IconCommon className={styles.icon} type={iconType} />
    </BaseButton>
  );
};
SubHeader.TextButton = ({ className, ...props }) => {
  return <TextButton.Secondary className={clsx(styles.textButton, className)} {...props} />;
};
// deprecated
SubHeader.ActionText = ({ className, ...props }) => {
  return <SunnyLink className={clsx(styles.actionText, className)} {...props} />;
};

export default SubHeader;
